// client/src/components/Insights/InsightsSummaryCard.js
// Compact insights summary for dashboard

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Brain,
  TrendingUp,
  AlertTriangle,
  Repeat,
  DollarSign,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';
import { useCurrency } from '../../contexts/CurrencyContext';

const InsightsSummaryCard = ({ insights, loading }) => {
  const { currency } = useCurrency(); // eslint-disable-line no-unused-vars
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  if (!insights || loading) return null;

  // Calculate counts
  const spendingCount = insights.spending?.length || 0;
  const anomaliesCount = insights.anomalies?.length || 0;
  const recurringCount = insights.recurring?.length || 0;
  const hasPrediction = insights.balancePrediction ? 1 : 0;
  const totalCount = spendingCount + anomaliesCount + recurringCount + hasPrediction;

  if (totalCount === 0) return null;

  // Get most important insight
  const getTopInsight = () => {
    // Priority: Anomalies > Spending changes > Recurring > Prediction
    if (anomaliesCount > 0) {
      const topAnomaly = insights.anomalies[0];
      return {
        icon: <AlertTriangle size={18} />,
        color: '#ef4444',
        text: `${anomaliesCount} unusual transaction${anomaliesCount > 1 ? 's' : ''} detected`,
        severity: 'high'
      };
    }
    
    if (spendingCount > 0) {
      const topSpending = insights.spending[0];
      return {
        icon: <TrendingUp size={18} />,
        color: topSpending.changeType === 'decrease' ? '#10b981' : '#f59e0b',
        text: topSpending.message.substring(0, 60) + '...',
        severity: topSpending.severity
      };
    }
    
    if (recurringCount > 0) {
      return {
        icon: <Repeat size={18} />,
        color: '#3b82f6',
        text: `${recurringCount} recurring transaction${recurringCount > 1 ? 's' : ''} found`,
        severity: 'info'
      };
    }
    
    if (hasPrediction) {
      const prediction = insights.balancePrediction;
      return {
        icon: <DollarSign size={18} />,
        color: prediction.balanceChange >= 0 ? '#10b981' : '#f59e0b',
        text: `Balance predicted: ${formatCurrency(Math.abs(prediction.predictedBalance))} in ${prediction.daysAhead} days`,
        severity: prediction.severity
      };
    }
    
    return null;
  };

  const topInsight = getTopInsight();

  return (
    <Card
      sx={{
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
        border: '2px solid',
        borderColor: theme.palette.mode === 'dark' 
          ? 'rgba(139, 92, 246, 0.3)' 
          : 'rgba(139, 92, 246, 0.2)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'visible',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 12px 24px rgba(139, 92, 246, 0.25)'
            : '0 12px 24px rgba(139, 92, 246, 0.15)'
        }
      }}
      onClick={() => navigate('/dashboard/insights')}
    >
      {/* AI Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: -12,
          right: isMobile ? 16 : 24,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
          color: 'white',
          px: 2,
          py: 0.5,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: isMobile ? '0.7rem' : '0.75rem',
          fontWeight: 700,
          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
        }}
      >
        <Sparkles size={14} />
        AI POWERED
      </Box>

      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Brain size={isMobile ? 24 : 28} color="white" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" sx={{ mb: 0.5 }}>
              AI Insights Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCount} insight{totalCount > 1 ? 's' : ''} generated from your spending patterns
            </Typography>
          </Box>
        </Box>

        {/* Top Insight Preview */}
        {topInsight && (
          <Box
            sx={{
              p: isMobile ? 1.5 : 2,
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(0, 0, 0, 0.3)'
                : 'rgba(255, 255, 255, 0.6)',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Box sx={{ color: topInsight.color }}>
              {topInsight.icon}
            </Box>
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                fontSize: isMobile ? '0.8rem' : '0.875rem',
                lineHeight: 1.5
              }}
            >
              {topInsight.text}
            </Typography>
          </Box>
        )}

        {/* Insight Counts */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {spendingCount > 0 && (
            <Chip
              icon={<TrendingUp size={14} />}
              label={`${spendingCount} Spending`}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(16, 185, 129, 0.15)'
                  : 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            />
          )}
          {anomaliesCount > 0 && (
            <Chip
              icon={<AlertTriangle size={14} />}
              label={`${anomaliesCount} Anomalies`}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(239, 68, 68, 0.15)'
                  : 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            />
          )}
          {recurringCount > 0 && (
            <Chip
              icon={<Repeat size={14} />}
              label={`${recurringCount} Recurring`}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(59, 130, 246, 0.15)'
                  : 'rgba(59, 130, 246, 0.1)',
                color: '#3b82f6',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            />
          )}
          {hasPrediction > 0 && (
            <Chip
              icon={<DollarSign size={14} />}
              label="Prediction"
              size="small"
              sx={{
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(139, 92, 246, 0.15)'
                  : 'rgba(139, 92, 246, 0.1)',
                color: '#8b5cf6',
                fontSize: isMobile ? '0.7rem' : '0.75rem'
              }}
            />
          )}
        </Box>

        {/* View All Button */}
        <Button
          fullWidth
          variant="contained"
          endIcon={<ChevronRight size={18} />}
          sx={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
            color: 'white',
            fontWeight: 600,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)'
            }
          }}
        >
          View All Insights
        </Button>
      </CardContent>
    </Card>
  );
};

export default InsightsSummaryCard;
